/**
 * Harmonic Drift - Configuration
 * Central configuration for the application
 */

export const CONFIG = {
    baseTempo: 110,
    globalSpeedMult: 1.0,
    connectionDistance: 150,
    
    // Physics-based modulation settings
    physicsModulation: {
        pitchRangeOctaves: 2,     // How many octaves velocity can shift
        velocityPitchFactor: 0.5, // How much velocity affects pitch
        connectionPitchFactor: 0.25, // How much connections affect pitch
        maxConnections: 5,        // Max connections for normalization
        positionDetuneRange: 50,  // Cents of detuning based on X position
        connectionReverbBoost: 0.15 // Reverb increase per connection
    },

    melodicPhysics: {
        // Call and response between connected nodes
        enableCallResponse: true,
        responseIntervals: [-4, -2, 0, 2, 3, 4, 5, 7], // Consonant intervals in scale degrees
        
        // Velocity-based rhythm modulation
        velocityRhythmFactor: 0.8, // How much velocity increases rhythm density
        velocityTriggerBonus: 0.3, // Extra trigger probability from velocity
        
        // Connection-based pitch influence
        connectionPitchPull: 0.4, // How strongly connections pull pitch toward center
        
        // Harmonic gravity - tendency toward consonant scale degrees
        harmonicGravityStrength: 0.3, // Probability of applying harmonic gravity
        
        // Melodic memory influence
        motifRepetitionChance: 0.15, // Chance to repeat recent melodic fragment
        
        // Collision harmony settings
        collisionHarmonyEnabled: true,
        collisionIntervals: [3, 4, 5, 7], // Intervals to use for collision harmonies
        
        // Position-based mode shifting
        positionModeInfluence: 0.5, // How much X position affects scale degree selection
        
        // Acceleration articulation
        accelerationDurationFactor: 0.5, // How much acceleration shortens notes
        
        // Cluster voicing for groups
        clusterVoicingThreshold: 3, // Min connections for cluster voicing
        clusterVoicingSpread: 0.7 // How spread out cluster voicings are
    },
    
    // Predefined note patterns for looping instruments
    patterns: {
        // Basic patterns
        arpeggio: [0, 2, 4, 5],
        bassline: [0, 0, 3, 5],
        melody: [0, 2, 4, 7, 4, 2],
        rhythm: [0, -1, 0, -1, 2, -1, 0, -1],
        drone: [0],
        chord: [[0, 2, 4]],
        sequence: [0, 4, 7, 4, 2, 5, 4, 0],
        pentatonic: [0, 1, 2, 3, 4],
        octaves: [0, 7],
        thirds: [0, 2, 4, 2],
        
        // Advanced melodic patterns
        ascend: [0, 1, 2, 3, 4, 5, 6, 7],
        descend: [7, 6, 5, 4, 3, 2, 1, 0],
        wave: [0, 2, 4, 6, 4, 2, 0, -2],
        bounce: [0, 4, 2, 5, 1, 6, 3, 7],
        zigzag: [0, 7, 1, 6, 2, 5, 3, 4],
        spiral: [0, 1, 3, 2, 4, 3, 5, 4],
        
        // Rhythmic patterns
        fourOnFloor: [0, -1, -1, -1, 0, -1, -1, -1],
        offbeat: [-1, 0, -1, 0, -1, 0, -1, 0],
        syncopated: [0, -1, 0, -1, -1, 0, -1, 0],
        shuffle: [0, -1, -1, 0, -1, -1, 0, -1],
        breakbeat: [0, -1, 2, -1, 0, 3, -1, 2],
        triplet: [0, -1, 0, 0, -1, 0],
        
        // Bass patterns
        deepBass: [0, 0, -1, 0, 2, 0, -1, 3],
        walking: [0, 2, 3, 5, 7, 5, 3, 2],
        pulseBass: [0, 0, 0, 0, 3, 3, 5, 5],
        rootFifth: [0, 0, 4, 4, 0, 0, 4, 0],
        
        // Chord patterns
        majorTriad: [[0, 2, 4]],
        minorTriad: [[0, 2, 3]],
        seventh: [[0, 2, 4, 6]],
        sus4: [[0, 3, 4]],
        powerChord: [[0, 4]],
        openVoicing: [[0, 4, 7, 11]],
        
        // Ambient patterns
        sparse: [0, -1, -1, -1, -1, -1, 2, -1],
        breath: [0, -1, -1, -1, -1, -1, -1, -1, 0, -1, -1, -1, -1, -1, -1, -1],
        shimmer: [0, 7, 0, 7, 2, 7, 2, 7],
        floating: [0, -1, 4, -1, 2, -1, 5, -1],
        
        // Percussive patterns  
        clave: [0, -1, -1, 0, -1, -1, 0, -1, -1, -1, 0, -1, 0, -1, -1, -1],
        tresillo: [0, -1, -1, 0, -1, -1, 0, -1],
        habanera: [0, -1, 0, -1, -1, 0, -1, 0],
        
        // Generative-style patterns
        fibonacci: [0, 1, 1, 2, 3, 5, 8, 5, 3, 2, 1, 1],
        primes: [2, 3, 5, 7, 11 % 8, 13 % 8],
        random8: [0, 5, 2, 7, 1, 4, 6, 3]
    },
    
    scales: {
        dorian: ['C3', 'D3', 'Eb3', 'F3', 'G3', 'A3', 'Bb3', 'C4'],
        pentatonic: ['C3', 'Eb3', 'F3', 'G3', 'Bb3', 'C4', 'Eb4', 'F4'],
        minor: ['C3', 'D3', 'Eb3', 'F3', 'G3', 'Ab3', 'Bb3', 'C4'],
        major: ['C3', 'D3', 'E3', 'F3', 'G3', 'A3', 'B3', 'C4']
    },
    
    physics: {
        friction: 0.99,
        collisionResponse: 0.5,
        entropyGrowthRate: 0.005,
        entropyDecayRate: 0.05,
        entropyInteractionTimeout: 5000 // ms
    },
    
    audio: {
        defaultVolume: -8,
        noiseVolume: -15,
        metalVolume: -12,
        reverbDecay: 4,
        reverbWet: 0.3,
        delayFeedback: 0.2
    }
};

// Synth types that cannot use PolySynth wrapper
export const NON_POLY_SYNTHS = [
    'NoiseSynth', 
    'MonoSynth', 
    'DuoSynth', 
    'MetalSynth', 
    'PluckSynth', 
    'MembraneSynth',
    'FMSynth',
    'AMSynth'
];

// Default instrument definitions
export const DEFAULT_INSTRUMENTS = [
    { 
        id: 'def_bass', 
        name: 'Bass', 
        icon: 'B', 
        color: '#00f3ff', 
        toneType: 'MonoSynth', 
        behavior: 'heavy', 
        options: { 
            oscillator: { type: "fatsawtooth", count: 3, spread: 20 }, 
            envelope: { attack: 0.01, decay: 0.4, sustain: 0.2, release: 1.5 }, 
            filterEnvelope: { attack: 0.01, decay: 0.1, sustain: 0.1, baseFrequency: 150, octaves: 3 } 
        } 
    },
    { 
        id: 'def_lead', 
        name: 'Lead', 
        icon: 'L', 
        color: '#ff0055', 
        toneType: 'Synth', 
        behavior: 'agile', 
        options: { 
            oscillator: { type: "fatsawtooth", count: 3, spread: 30 }, 
            envelope: { attack: 0.02, decay: 0.3, sustain: 0.2, release: 1.0 } 
        } 
    },
    { 
        id: 'def_pad', 
        name: 'Pad', 
        icon: 'P', 
        color: '#00ff88', 
        toneType: 'AMSynth', 
        behavior: 'floaty', 
        options: { 
            harmonicity: 1.5, 
            oscillator: { type: "sine" }, 
            envelope: { attack: 0.8, decay: 1, sustain: 0.8, release: 4 }, 
            modulation: { type: "sine" } 
        } 
    },
    { 
        id: 'def_kick', 
        name: 'Kick', 
        icon: 'K', 
        color: '#ffcc00', 
        toneType: 'MembraneSynth', 
        behavior: 'normal', 
        options: { 
            pitchDecay: 0.04, 
            octaves: 6, 
            oscillator: { type: "sine" }, 
            envelope: { attack: 0.001, decay: 0.3, sustain: 0.01, release: 1.0 } 
        } 
    },
    { 
        id: 'def_metal', 
        name: 'Metal', 
        icon: 'M', 
        color: '#a0a0a0', 
        toneType: 'MetalSynth', 
        behavior: 'normal', 
        options: { 
            frequency: 150, 
            envelope: { attack: 0.001, decay: 0.2, release: 0.05 }, 
            harmonicity: 4.1, 
            modulationIndex: 20, 
            resonance: 3000, 
            octaves: 1.5 
        } 
    },
    { 
        id: 'def_pluck', 
        name: 'Pluck', 
        icon: 'S', 
        color: '#ff8800', 
        toneType: 'PluckSynth', 
        behavior: 'agile', 
        options: { 
            attackNoise: 1, 
            dampening: 3500, 
            resonance: 0.99 
        } 
    },
    { 
        id: 'def_drone', 
        name: 'Drone', 
        icon: 'D', 
        color: '#9900ff', 
        toneType: 'FMSynth', 
        behavior: 'floaty', 
        options: { 
            harmonicity: 1, 
            modulationIndex: 15, 
            detune: 0, 
            oscillator: { type: "sine" }, 
            envelope: { attack: 2, decay: 1, sustain: 1, release: 3 }, 
            modulation: { type: "sawtooth" }, 
            modulationEnvelope: { attack: 1, decay: 0, sustain: 1, release: 1 } 
        } 
    },
    { 
        id: 'def_duo', 
        name: 'Duo', 
        icon: '2', 
        color: '#ff00cc', 
        toneType: 'DuoSynth', 
        behavior: 'normal', 
        options: { 
            vibratoAmount: 0.3, 
            vibratoRate: 5, 
            harmonicity: 1.01, 
            voice0: { 
                volume: -10, 
                portamento: 0, 
                oscillator: { type: "sawtooth" }, 
                filterEnvelope: { attack: 0.01, decay: 0.2, sustain: 0.2, release: 0.5 } 
            }, 
            voice1: { 
                volume: -10, 
                portamento: 0, 
                oscillator: { type: "square" }, 
                filterEnvelope: { attack: 0.01, decay: 0.2, sustain: 0.2, release: 0.5 } 
            } 
        } 
    },
    { 
        id: 'def_noise', 
        name: 'Noise', 
        icon: 'N', 
        color: '#ffffff', 
        toneType: 'NoiseSynth', 
        behavior: 'normal', 
        options: { 
            noise: { type: 'white' }, 
            envelope: { attack: 0.001, decay: 0.05, sustain: 0.1, release: 0.2 } 
        } 
    },
    { 
        id: 'def_am', 
        name: 'AM', 
        icon: 'A', 
        color: '#ccff00', 
        toneType: 'AMSynth', 
        behavior: 'normal', 
        options: { 
            harmonicity: 2, 
            detune: 0, 
            oscillator: { type: "sine" }, 
            envelope: { attack: 0.01, decay: 0.5, sustain: 0.2, release: 0.5 }, 
            modulation: { type: "square" }, 
            modulationEnvelope: { attack: 0.2, decay: 0.1, sustain: 0.5, release: 0.5 } 
        } 
    },
    { 
        id: 'def_fm', 
        name: 'FM', 
        icon: 'F', 
        color: '#7b00ff', 
        toneType: 'FMSynth', 
        behavior: 'normal', 
        options: { 
            harmonicity: 3, 
            modulationIndex: 10, 
            oscillator: { type: "sine" }, 
            envelope: { attack: 0.01, decay: 1, sustain: 0.1, release: 1 }, 
            modulation: { type: "triangle" }, 
            modulationEnvelope: { attack: 0.01, decay: 0.5, sustain: 0, release: 0.5 } 
        } 
    },
    { 
        id: 'def_memb', 
        name: 'Memb', 
        icon: 'Mb', 
        color: '#ff4d00', 
        toneType: 'MembraneSynth', 
        behavior: 'normal', 
        options: { 
            pitchDecay: 0.03, 
            octaves: 3, 
            oscillator: { type: "triangle" }, 
            envelope: { attack: 0.001, decay: 0.2, sustain: 0.01, release: 1.0 } 
        } 
    },
    { 
        id: 'def_pulse', 
        name: 'Pulse', 
        icon: 'Pu', 
        color: '#008080', 
        toneType: 'Synth', 
        behavior: 'normal', 
        options: { 
            oscillator: { type: "pulse", width: 0.2 }, 
            envelope: { attack: 0.01, decay: 0.2, sustain: 0.2, release: 0.5 } 
        } 
    },
    { 
        id: 'def_saw', 
        name: 'Saw', 
        icon: 'Sw', 
        color: '#dc143c', 
        toneType: 'Synth', 
        behavior: 'normal', 
        options: { 
            oscillator: { type: "fatsawtooth", count: 5, spread: 40 }, 
            envelope: { attack: 0.01, decay: 0.5, sustain: 0.1, release: 1 } 
        } 
    },
    { 
        id: 'def_sine', 
        name: 'Sine', 
        icon: 'Si', 
        color: '#87cefa', 
        toneType: 'Synth', 
        behavior: 'floaty', 
        options: { 
            oscillator: { type: "sine" }, 
            envelope: { attack: 0.2, decay: 0.8, sustain: 0.5, release: 1.5 } 
        } 
    },
    { 
        id: 'def_wood', 
        name: 'Wood', 
        icon: 'W', 
        color: '#deb887', 
        toneType: 'Synth', 
        behavior: 'normal', 
        options: { 
            oscillator: { type: "triangle" }, 
            envelope: { attack: 0.001, decay: 0.08, sustain: 0, release: 0.05 } 
        } 
    },
    { 
        id: 'def_zap', 
        name: 'Zap', 
        icon: 'Z', 
        color: '#ffff00', 
        toneType: 'Synth', 
        behavior: 'agile', 
        options: { 
            oscillator: { type: "sawtooth" }, 
            envelope: { attack: 0.001, decay: 0.05, sustain: 0, release: 0.1 } 
        } 
    },
    { 
        id: 'def_glass', 
        name: 'Glass', 
        icon: 'G', 
        color: '#e0ffff', 
        toneType: 'FMSynth', 
        behavior: 'floaty', 
        options: { 
            harmonicity: 5.1, 
            modulationIndex: 10, 
            oscillator: { type: "sine" }, 
            envelope: { attack: 0.001, decay: 0.7, sustain: 0.1, release: 1.5 }, 
            modulation: { type: "sine" }, 
            modulationEnvelope: { attack: 0.2, decay: 0, sustain: 1, release: 0.5 } 
        } 
    },
    { 
        id: 'def_grit', 
        name: 'Grit', 
        icon: 'Gr', 
        color: '#696969', 
        toneType: 'Synth', 
        behavior: 'normal', 
        options: { 
            oscillator: { type: "fatsquare", count: 3, spread: 40 }, 
            envelope: { attack: 0.01, decay: 0.1, sustain: 0.3, release: 0.2 } 
        } 
    },
    { 
        id: 'def_sub', 
        name: 'Sub', 
        icon: 'Sb', 
        color: '#00008b', 
        toneType: 'MonoSynth', 
        behavior: 'heavy', 
        options: { 
            oscillator: { type: "sine" }, 
            envelope: { attack: 0.01, decay: 0.3, sustain: 0.9, release: 1 }, 
            filterEnvelope: { attack: 0.001, decay: 0.01, sustain: 1, baseFrequency: 60, octaves: 1 } 
        } 
    },
    { 
        id: 'sys_battery', 
        name: 'Battery', 
        icon: '🔋', 
        color: '#ffff00', 
        toneType: 'Synth', 
        behavior: 'static', 
        options: { 
            oscillator: { type: "sine" }, 
            envelope: { attack: 0.01, decay: 0.1, sustain: 0, release: 0.1 } 
        } 
    },
    // === LOOPING INSTRUMENTS WITH PHYSICS MODULATION ===
    {
        id: 'def_loop_arp',
        name: 'Arp Loop',
        icon: 'AL',
        color: '#ff69b4',
        toneType: 'Synth',
        behavior: 'agile',
        looping: true,
        pattern: 'arpeggio',
        patternSpeed: 4, // Steps per beat
        physicsModulation: {
            velocityToPitch: true,   // Velocity affects pitch
            positionToDetune: true,  // X position affects detuning
            connectionsToReverb: true // More connections = more reverb
        },
        options: {
            oscillator: { type: "triangle" },
            envelope: { attack: 0.01, decay: 0.2, sustain: 0.1, release: 0.3 }
        }
    },
    {
        id: 'def_loop_bass',
        name: 'Bass Loop',
        icon: 'BL',
        color: '#4169e1',
        toneType: 'MonoSynth',
        behavior: 'heavy',
        looping: true,
        pattern: 'bassline',
        patternSpeed: 2,
        physicsModulation: {
            velocityToFilter: true,  // Velocity affects filter cutoff
            connectionsToOctave: true // More connections = lower octave
        },
        options: {
            oscillator: { type: "sawtooth" },
            envelope: { attack: 0.05, decay: 0.3, sustain: 0.6, release: 0.5 },
            filterEnvelope: { attack: 0.01, decay: 0.3, sustain: 0.3, baseFrequency: 200, octaves: 3 }
        }
    },
    {
        id: 'def_loop_melody',
        name: 'Melody',
        icon: 'ML',
        color: '#ffd700',
        toneType: 'FMSynth',
        behavior: 'floaty',
        looping: true,
        pattern: 'melody',
        patternSpeed: 2,
        physicsModulation: {
            velocityToPitch: true,
            positionToHarmonicity: true, // Y position affects FM harmonicity
            connectionsToModIndex: true  // Connections affect modulation
        },
        options: {
            harmonicity: 2,
            modulationIndex: 5,
            oscillator: { type: "sine" },
            envelope: { attack: 0.1, decay: 0.3, sustain: 0.4, release: 1 },
            modulation: { type: "sine" },
            modulationEnvelope: { attack: 0.2, decay: 0.3, sustain: 0.5, release: 0.8 }
        }
    },
    {
        id: 'def_loop_pulse',
        name: 'Pulse Seq',
        icon: 'PS',
        color: '#00ff7f',
        toneType: 'Synth',
        behavior: 'normal',
        looping: true,
        pattern: 'rhythm',
        patternSpeed: 4,
        physicsModulation: {
            velocityToVolume: true,  // Faster = louder
            positionToDetune: true,
            connectionsToPan: true   // Connections affect stereo spread
        },
        options: {
            oscillator: { type: "pulse", width: 0.3 },
            envelope: { attack: 0.001, decay: 0.1, sustain: 0.05, release: 0.2 }
        }
    },
    {
        id: 'def_loop_chord',
        name: 'Chord Pad',
        icon: 'CP',
        color: '#da70d6',
        toneType: 'AMSynth',
        behavior: 'floaty',
        looping: true,
        pattern: 'chord',
        patternSpeed: 0.5, // Slow chord changes
        physicsModulation: {
            positionToDetune: true,
            connectionsToReverb: true,
            velocityToAttack: true  // Slower when moving fast
        },
        options: {
            harmonicity: 2,
            oscillator: { type: "sine" },
            envelope: { attack: 0.8, decay: 0.5, sustain: 1, release: 2 },
            modulation: { type: "sine" }
        }
    },
    {
        id: 'def_loop_seq',
        name: 'Sequencer',
        icon: 'SQ',
        color: '#ff4500',
        toneType: 'Synth',
        behavior: 'agile',
        looping: true,
        pattern: 'sequence',
        patternSpeed: 4,
        physicsModulation: {
            velocityToPitch: true,
            positionToDetune: true,
            connectionsToPattern: true // Connections can shift pattern
        },
        options: {
            oscillator: { type: "square" },
            envelope: { attack: 0.01, decay: 0.15, sustain: 0.1, release: 0.3 }
        }
    },
    {
        id: 'def_loop_penta',
        name: 'Penta Walk',
        icon: 'PW',
        color: '#20b2aa',
        toneType: 'PluckSynth',
        behavior: 'normal',
        looping: true,
        pattern: 'pentatonic',
        patternSpeed: 3,
        physicsModulation: {
            velocityToDampening: true, // Velocity affects pluck dampening
            connectionsToResonance: true
        },
        options: {
            attackNoise: 2,
            dampening: 3000,
            resonance: 0.95
        }
    },
    {
        id: 'def_loop_oct',
        name: 'Oct Jump',
        icon: 'OJ',
        color: '#8a2be2',
        toneType: 'DuoSynth',
        behavior: 'normal',
        looping: true,
        pattern: 'octaves',
        patternSpeed: 2,
        physicsModulation: {
            velocityToVibratoAmount: true,
            connectionsToHarmonicity: true
        },
        options: {
            vibratoAmount: 0.2,
            vibratoRate: 4,
            harmonicity: 1.5,
            voice0: {
                volume: -8,
                oscillator: { type: "sine" },
                envelope: { attack: 0.05, decay: 0.3, sustain: 0.4, release: 0.8 }
            },
            voice1: {
                volume: -8,
                oscillator: { type: "triangle" },
                envelope: { attack: 0.05, decay: 0.3, sustain: 0.4, release: 0.8 }
            }
        }
    },
    {
        id: 'def_loop_thirds',
        name: 'Thirds',
        icon: 'TH',
        color: '#f0e68c',
        toneType: 'Synth',
        behavior: 'floaty',
        looping: true,
        pattern: 'thirds',
        patternSpeed: 1.5,
        physicsModulation: {
            velocityToPitch: true,
            positionToDetune: true,
            connectionsToReverb: true
        },
        options: {
            oscillator: { type: "sine" },
            envelope: { attack: 0.3, decay: 0.4, sustain: 0.6, release: 1.5 }
        }
    },

    // ╔════════════════════════════════════════════════════════════════╗
    // ║                    CATEGORY: BASS                              ║
    // ╚════════════════════════════════════════════════════════════════╝
    
    // --- Subcategory: Deep Bass ---
    {
        id: 'cat_bass_subterranean',
        name: 'Subterranean',
        icon: '⬇',
        color: '#1a1a4e',
        category: 'bass',
        subcategory: 'deep',
        toneType: 'MonoSynth',
        behavior: 'heavy',
        looping: true,
        pattern: 'deepBass',
        patternSpeed: 1,
        physicsModulation: {
            velocityToFilter: true,
            connectionsToOctave: true
        },
        options: {
            oscillator: { type: "sine" },
            envelope: { attack: 0.2, decay: 0.5, sustain: 0.9, release: 1.5 },
            filterEnvelope: { attack: 0.01, decay: 0.3, sustain: 0.4, baseFrequency: 50, octaves: 1.5 }
        }
    },
    {
        id: 'cat_bass_rumble',
        name: 'Rumble',
        icon: '◉',
        color: '#2d1b4e',
        category: 'bass',
        subcategory: 'deep',
        toneType: 'FMSynth',
        behavior: 'heavy',
        looping: true,
        pattern: 'pulseBass',
        patternSpeed: 2,
        physicsModulation: {
            velocityToFilter: true,
            connectionsToModIndex: true
        },
        options: {
            harmonicity: 0.5,
            modulationIndex: 3,
            oscillator: { type: "sine" },
            envelope: { attack: 0.1, decay: 0.4, sustain: 0.7, release: 1 },
            modulation: { type: "sine" },
            modulationEnvelope: { attack: 0.2, decay: 0.3, sustain: 0.6, release: 0.8 }
        }
    },
    {
        id: 'cat_bass_abyss',
        name: 'Abyss',
        icon: '▼',
        color: '#0d0d2b',
        category: 'bass',
        subcategory: 'deep',
        toneType: 'MonoSynth',
        behavior: 'heavy',
        options: {
            oscillator: { type: "triangle" },
            envelope: { attack: 0.3, decay: 0.6, sustain: 0.8, release: 2 },
            filterEnvelope: { attack: 0.1, decay: 0.5, sustain: 0.3, baseFrequency: 40, octaves: 1 }
        }
    },
    
    // --- Subcategory: Punchy Bass ---
    {
        id: 'cat_bass_thump',
        name: 'Thump',
        icon: '●',
        color: '#4a1942',
        category: 'bass',
        subcategory: 'punchy',
        toneType: 'MonoSynth',
        behavior: 'normal',
        looping: true,
        pattern: 'fourOnFloor',
        patternSpeed: 2,
        physicsModulation: {
            velocityToVolume: true
        },
        options: {
            oscillator: { type: "square" },
            envelope: { attack: 0.001, decay: 0.2, sustain: 0.3, release: 0.3 },
            filterEnvelope: { attack: 0.001, decay: 0.15, sustain: 0.1, baseFrequency: 300, octaves: 3 }
        }
    },
    {
        id: 'cat_bass_slap',
        name: 'Slap',
        icon: '◐',
        color: '#6b2d5b',
        category: 'bass',
        subcategory: 'punchy',
        toneType: 'PluckSynth',
        behavior: 'agile',
        looping: true,
        pattern: 'walking',
        patternSpeed: 3,
        physicsModulation: {
            velocityToDampening: true,
            connectionsToResonance: true
        },
        options: {
            attackNoise: 4,
            dampening: 2500,
            resonance: 0.92
        }
    },
    {
        id: 'cat_bass_808',
        name: '808',
        icon: '◆',
        color: '#e63946',
        category: 'bass',
        subcategory: 'punchy',
        toneType: 'MembraneSynth',
        behavior: 'heavy',
        options: {
            pitchDecay: 0.08,
            octaves: 6,
            oscillator: { type: "sine" },
            envelope: { attack: 0.001, decay: 0.8, sustain: 0.01, release: 1.2 }
        }
    },

    // --- Subcategory: Synth Bass ---
    {
        id: 'cat_bass_reese',
        name: 'Reese',
        icon: '≈',
        color: '#7b2d8e',
        category: 'bass',
        subcategory: 'synth',
        toneType: 'DuoSynth',
        behavior: 'heavy',
        looping: true,
        pattern: 'rootFifth',
        patternSpeed: 1,
        physicsModulation: {
            velocityToVibratoAmount: true,
            connectionsToHarmonicity: true
        },
        options: {
            vibratoAmount: 0.3,
            vibratoRate: 3,
            harmonicity: 1.01,
            voice0: { volume: -6, oscillator: { type: "sawtooth" }, envelope: { attack: 0.05, decay: 0.3, sustain: 0.6, release: 0.8 } },
            voice1: { volume: -6, oscillator: { type: "sawtooth" }, envelope: { attack: 0.05, decay: 0.3, sustain: 0.6, release: 0.8 } }
        }
    },
    {
        id: 'cat_bass_acid',
        name: 'Acid',
        icon: '⚗',
        color: '#39ff14',
        category: 'bass',
        subcategory: 'synth',
        toneType: 'MonoSynth',
        behavior: 'agile',
        looping: true,
        pattern: 'syncopated',
        patternSpeed: 4,
        physicsModulation: {
            velocityToFilter: true,
            positionToDetune: true
        },
        options: {
            oscillator: { type: "sawtooth" },
            envelope: { attack: 0.001, decay: 0.2, sustain: 0.2, release: 0.2 },
            filterEnvelope: { attack: 0.001, decay: 0.3, sustain: 0.1, baseFrequency: 100, octaves: 4 }
        }
    },

    // ╔════════════════════════════════════════════════════════════════╗
    // ║                    CATEGORY: LEADS                             ║
    // ╚════════════════════════════════════════════════════════════════╝
    
    // --- Subcategory: Bright Leads ---
    {
        id: 'cat_lead_laser',
        name: 'Laser',
        icon: '⚡',
        color: '#00ffff',
        category: 'leads',
        subcategory: 'bright',
        toneType: 'Synth',
        behavior: 'agile',
        looping: true,
        pattern: 'zigzag',
        patternSpeed: 6,
        physicsModulation: {
            velocityToPitch: true,
            positionToDetune: true
        },
        options: {
            oscillator: { type: "sawtooth" },
            envelope: { attack: 0.001, decay: 0.1, sustain: 0.3, release: 0.2 }
        }
    },
    {
        id: 'cat_lead_neon',
        name: 'Neon',
        icon: '◈',
        color: '#ff1493',
        category: 'leads',
        subcategory: 'bright',
        toneType: 'Synth',
        behavior: 'agile',
        looping: true,
        pattern: 'bounce',
        patternSpeed: 4,
        physicsModulation: {
            velocityToPitch: true,
            connectionsToReverb: true
        },
        options: {
            oscillator: { type: "fatsawtooth", count: 3, spread: 40 },
            envelope: { attack: 0.01, decay: 0.2, sustain: 0.4, release: 0.5 }
        }
    },
    {
        id: 'cat_lead_crystal',
        name: 'Crystal',
        icon: '❖',
        color: '#e0ffff',
        category: 'leads',
        subcategory: 'bright',
        toneType: 'FMSynth',
        behavior: 'floaty',
        looping: true,
        pattern: 'shimmer',
        patternSpeed: 3,
        physicsModulation: {
            positionToHarmonicity: true,
            connectionsToModIndex: true
        },
        options: {
            harmonicity: 4,
            modulationIndex: 8,
            oscillator: { type: "sine" },
            envelope: { attack: 0.01, decay: 0.3, sustain: 0.2, release: 1.5 },
            modulation: { type: "sine" },
            modulationEnvelope: { attack: 0.3, decay: 0.2, sustain: 0.3, release: 1 }
        }
    },

    // --- Subcategory: Warm Leads ---
    {
        id: 'cat_lead_analog',
        name: 'Analog',
        icon: '∿',
        color: '#ffa07a',
        category: 'leads',
        subcategory: 'warm',
        toneType: 'MonoSynth',
        behavior: 'normal',
        looping: true,
        pattern: 'melody',
        patternSpeed: 2,
        physicsModulation: {
            velocityToFilter: true,
            positionToDetune: true
        },
        options: {
            oscillator: { type: "sawtooth" },
            envelope: { attack: 0.05, decay: 0.3, sustain: 0.5, release: 0.8 },
            filterEnvelope: { attack: 0.05, decay: 0.4, sustain: 0.3, baseFrequency: 800, octaves: 2 }
        }
    },
    {
        id: 'cat_lead_vintage',
        name: 'Vintage',
        icon: '♫',
        color: '#daa520',
        category: 'leads',
        subcategory: 'warm',
        toneType: 'AMSynth',
        behavior: 'normal',
        looping: true,
        pattern: 'ascend',
        patternSpeed: 2,
        physicsModulation: {
            velocityToPitch: true,
            connectionsToReverb: true
        },
        options: {
            harmonicity: 2,
            oscillator: { type: "sine" },
            envelope: { attack: 0.1, decay: 0.3, sustain: 0.6, release: 1 },
            modulation: { type: "sine" }
        }
    },
    {
        id: 'cat_lead_mellow',
        name: 'Mellow',
        icon: '○',
        color: '#f4a460',
        category: 'leads',
        subcategory: 'warm',
        toneType: 'Synth',
        behavior: 'floaty',
        looping: true,
        pattern: 'wave',
        patternSpeed: 1.5,
        physicsModulation: {
            velocityToAttack: true,
            positionToDetune: true
        },
        options: {
            oscillator: { type: "sine" },
            envelope: { attack: 0.2, decay: 0.4, sustain: 0.7, release: 1.5 }
        }
    },

    // --- Subcategory: Aggressive Leads ---
    {
        id: 'cat_lead_scream',
        name: 'Scream',
        icon: '⚠',
        color: '#ff0000',
        category: 'leads',
        subcategory: 'aggressive',
        toneType: 'FMSynth',
        behavior: 'agile',
        looping: true,
        pattern: 'breakbeat',
        patternSpeed: 4,
        physicsModulation: {
            velocityToPitch: true,
            velocityToVolume: true,
            connectionsToModIndex: true
        },
        options: {
            harmonicity: 3.5,
            modulationIndex: 20,
            oscillator: { type: "square" },
            envelope: { attack: 0.001, decay: 0.15, sustain: 0.2, release: 0.3 },
            modulation: { type: "sawtooth" },
            modulationEnvelope: { attack: 0.001, decay: 0.1, sustain: 0.3, release: 0.2 }
        }
    },
    {
        id: 'cat_lead_distort',
        name: 'Distort',
        icon: '▓',
        color: '#8b0000',
        category: 'leads',
        subcategory: 'aggressive',
        toneType: 'Synth',
        behavior: 'agile',
        looping: true,
        pattern: 'tresillo',
        patternSpeed: 4,
        physicsModulation: {
            velocityToVolume: true,
            positionToDetune: true
        },
        options: {
            oscillator: { type: "fatsquare", count: 3, spread: 50 },
            envelope: { attack: 0.001, decay: 0.1, sustain: 0.4, release: 0.2 }
        }
    },

    // ╔════════════════════════════════════════════════════════════════╗
    // ║                    CATEGORY: PADS                              ║
    // ╚════════════════════════════════════════════════════════════════╝
    
    // --- Subcategory: Ethereal Pads ---
    {
        id: 'cat_pad_heaven',
        name: 'Heaven',
        icon: '☁',
        color: '#87ceeb',
        category: 'pads',
        subcategory: 'ethereal',
        toneType: 'AMSynth',
        behavior: 'floaty',
        looping: true,
        pattern: 'breath',
        patternSpeed: 0.25,
        physicsModulation: {
            positionToDetune: true,
            connectionsToReverb: true
        },
        options: {
            harmonicity: 3,
            oscillator: { type: "sine" },
            envelope: { attack: 2, decay: 1, sustain: 1, release: 4 },
            modulation: { type: "sine" }
        }
    },
    {
        id: 'cat_pad_angel',
        name: 'Angel',
        icon: '✧',
        color: '#fff8dc',
        category: 'pads',
        subcategory: 'ethereal',
        toneType: 'FMSynth',
        behavior: 'floaty',
        looping: true,
        pattern: 'floating',
        patternSpeed: 0.5,
        physicsModulation: {
            positionToHarmonicity: true,
            connectionsToReverb: true
        },
        options: {
            harmonicity: 2.5,
            modulationIndex: 3,
            oscillator: { type: "sine" },
            envelope: { attack: 1.5, decay: 0.8, sustain: 1, release: 3 },
            modulation: { type: "sine" },
            modulationEnvelope: { attack: 1, decay: 0.5, sustain: 0.8, release: 2 }
        }
    },
    {
        id: 'cat_pad_aurora',
        name: 'Aurora',
        icon: '◇',
        color: '#98fb98',
        category: 'pads',
        subcategory: 'ethereal',
        toneType: 'Synth',
        behavior: 'floaty',
        looping: true,
        pattern: 'sparse',
        patternSpeed: 0.3,
        physicsModulation: {
            velocityToAttack: true,
            positionToDetune: true,
            connectionsToReverb: true
        },
        options: {
            oscillator: { type: "sine" },
            envelope: { attack: 3, decay: 2, sustain: 0.8, release: 5 }
        }
    },

    // --- Subcategory: Dark Pads ---
    {
        id: 'cat_pad_void',
        name: 'Void',
        icon: '◼',
        color: '#1c1c1c',
        category: 'pads',
        subcategory: 'dark',
        toneType: 'FMSynth',
        behavior: 'floaty',
        looping: true,
        pattern: 'drone',
        patternSpeed: 0.1,
        physicsModulation: {
            connectionsToModIndex: true,
            positionToHarmonicity: true
        },
        options: {
            harmonicity: 0.5,
            modulationIndex: 15,
            oscillator: { type: "sine" },
            envelope: { attack: 3, decay: 2, sustain: 1, release: 4 },
            modulation: { type: "sine" },
            modulationEnvelope: { attack: 2, decay: 1, sustain: 1, release: 3 }
        }
    },
    {
        id: 'cat_pad_shadow',
        name: 'Shadow',
        icon: '▪',
        color: '#2f2f2f',
        category: 'pads',
        subcategory: 'dark',
        toneType: 'DuoSynth',
        behavior: 'floaty',
        looping: true,
        pattern: 'sparse',
        patternSpeed: 0.2,
        physicsModulation: {
            connectionsToHarmonicity: true,
            velocityToVibratoAmount: true
        },
        options: {
            vibratoAmount: 0.1,
            vibratoRate: 2,
            harmonicity: 0.7,
            voice0: { volume: -8, oscillator: { type: "sine" }, envelope: { attack: 2, decay: 1, sustain: 0.9, release: 3 } },
            voice1: { volume: -10, oscillator: { type: "triangle" }, envelope: { attack: 2.5, decay: 1, sustain: 0.8, release: 4 } }
        }
    },
    {
        id: 'cat_pad_abyss',
        name: 'Deep Abyss',
        icon: '▬',
        color: '#0a0a1a',
        category: 'pads',
        subcategory: 'dark',
        toneType: 'MonoSynth',
        behavior: 'floaty',
        options: {
            oscillator: { type: "triangle" },
            envelope: { attack: 2, decay: 1.5, sustain: 0.9, release: 4 },
            filterEnvelope: { attack: 1, decay: 2, sustain: 0.3, baseFrequency: 100, octaves: 2 }
        }
    },

    // --- Subcategory: Evolving Pads ---
    {
        id: 'cat_pad_morph',
        name: 'Morph',
        icon: '◎',
        color: '#9370db',
        category: 'pads',
        subcategory: 'evolving',
        toneType: 'FMSynth',
        behavior: 'floaty',
        looping: true,
        pattern: 'fibonacci',
        patternSpeed: 0.5,
        physicsModulation: {
            positionToHarmonicity: true,
            velocityToAttack: true,
            connectionsToModIndex: true
        },
        options: {
            harmonicity: 1.5,
            modulationIndex: 8,
            oscillator: { type: "sine" },
            envelope: { attack: 1, decay: 1, sustain: 0.8, release: 2 },
            modulation: { type: "sine" },
            modulationEnvelope: { attack: 0.8, decay: 0.5, sustain: 0.7, release: 1.5 }
        }
    },
    {
        id: 'cat_pad_breathe',
        name: 'Breathe',
        icon: '❋',
        color: '#dda0dd',
        category: 'pads',
        subcategory: 'evolving',
        toneType: 'AMSynth',
        behavior: 'floaty',
        looping: true,
        pattern: 'breath',
        patternSpeed: 0.2,
        physicsModulation: {
            velocityToAttack: true,
            connectionsToReverb: true
        },
        options: {
            harmonicity: 2,
            oscillator: { type: "sine" },
            envelope: { attack: 2.5, decay: 1, sustain: 0.9, release: 3 },
            modulation: { type: "sine" }
        }
    },

    // ╔════════════════════════════════════════════════════════════════╗
    // ║                    CATEGORY: PERCUSSION                        ║
    // ╚════════════════════════════════════════════════════════════════╝
    
    // --- Subcategory: Kicks ---
    {
        id: 'cat_perc_punch',
        name: 'Punch',
        icon: '◉',
        color: '#ff4500',
        category: 'percussion',
        subcategory: 'kicks',
        toneType: 'MembraneSynth',
        behavior: 'normal',
        looping: true,
        pattern: 'fourOnFloor',
        patternSpeed: 2,
        physicsModulation: {
            velocityToVolume: true
        },
        options: {
            pitchDecay: 0.03,
            octaves: 5,
            oscillator: { type: "sine" },
            envelope: { attack: 0.001, decay: 0.3, sustain: 0.01, release: 0.8 }
        }
    },
    {
        id: 'cat_perc_boom',
        name: 'Boom',
        icon: '◕',
        color: '#cd5c5c',
        category: 'percussion',
        subcategory: 'kicks',
        toneType: 'MembraneSynth',
        behavior: 'heavy',
        looping: true,
        pattern: 'syncopated',
        patternSpeed: 2,
        physicsModulation: {
            velocityToVolume: true
        },
        options: {
            pitchDecay: 0.1,
            octaves: 8,
            oscillator: { type: "sine" },
            envelope: { attack: 0.001, decay: 0.5, sustain: 0.02, release: 1.5 }
        }
    },

    // --- Subcategory: Metallic ---
    {
        id: 'cat_perc_hihat',
        name: 'HiHat',
        icon: '◌',
        color: '#c0c0c0',
        category: 'percussion',
        subcategory: 'metallic',
        toneType: 'MetalSynth',
        behavior: 'agile',
        looping: true,
        pattern: 'offbeat',
        patternSpeed: 4,
        physicsModulation: {
            velocityToVolume: true
        },
        options: {
            frequency: 400,
            envelope: { attack: 0.001, decay: 0.05, release: 0.01 },
            harmonicity: 5.1,
            modulationIndex: 40,
            resonance: 3000,
            octaves: 1.2
        }
    },
    {
        id: 'cat_perc_ride',
        name: 'Ride',
        icon: '◯',
        color: '#d4af37',
        category: 'percussion',
        subcategory: 'metallic',
        toneType: 'MetalSynth',
        behavior: 'normal',
        looping: true,
        pattern: 'shuffle',
        patternSpeed: 3,
        physicsModulation: {
            velocityToVolume: true
        },
        options: {
            frequency: 300,
            envelope: { attack: 0.001, decay: 0.2, release: 0.1 },
            harmonicity: 3.1,
            modulationIndex: 25,
            resonance: 4500,
            octaves: 1
        }
    },
    {
        id: 'cat_perc_bell',
        name: 'Bell',
        icon: '◊',
        color: '#ffd700',
        category: 'percussion',
        subcategory: 'metallic',
        toneType: 'MetalSynth',
        behavior: 'floaty',
        looping: true,
        pattern: 'clave',
        patternSpeed: 2,
        physicsModulation: {
            velocityToVolume: true,
            connectionsToReverb: true
        },
        options: {
            frequency: 250,
            envelope: { attack: 0.001, decay: 0.4, release: 0.2 },
            harmonicity: 8,
            modulationIndex: 15,
            resonance: 5000,
            octaves: 0.8
        }
    },

    // --- Subcategory: Electronic Percussion ---
    {
        id: 'cat_perc_click',
        name: 'Click',
        icon: '·',
        color: '#f5f5f5',
        category: 'percussion',
        subcategory: 'electronic',
        toneType: 'Synth',
        behavior: 'agile',
        looping: true,
        pattern: 'triplet',
        patternSpeed: 6,
        physicsModulation: {
            velocityToVolume: true,
            positionToDetune: true
        },
        options: {
            oscillator: { type: "sine" },
            envelope: { attack: 0.001, decay: 0.02, sustain: 0, release: 0.02 }
        }
    },
    {
        id: 'cat_perc_zap',
        name: 'Zapper',
        icon: '⚡',
        color: '#7fff00',
        category: 'percussion',
        subcategory: 'electronic',
        toneType: 'Synth',
        behavior: 'agile',
        looping: true,
        pattern: 'breakbeat',
        patternSpeed: 4,
        physicsModulation: {
            velocityToPitch: true,
            velocityToVolume: true
        },
        options: {
            oscillator: { type: "sawtooth" },
            envelope: { attack: 0.001, decay: 0.08, sustain: 0, release: 0.05 }
        }
    },

    // ╔════════════════════════════════════════════════════════════════╗
    // ║                    CATEGORY: PLUCKED                           ║
    // ╚════════════════════════════════════════════════════════════════╝
    
    // --- Subcategory: Strings ---
    {
        id: 'cat_pluck_harp',
        name: 'Harp',
        icon: '♪',
        color: '#ffefd5',
        category: 'plucked',
        subcategory: 'strings',
        toneType: 'PluckSynth',
        behavior: 'floaty',
        looping: true,
        pattern: 'ascend',
        patternSpeed: 3,
        physicsModulation: {
            velocityToDampening: true,
            connectionsToResonance: true
        },
        options: {
            attackNoise: 0.5,
            dampening: 4500,
            resonance: 0.99
        }
    },
    {
        id: 'cat_pluck_guitar',
        name: 'Guitar',
        icon: '♩',
        color: '#cd853f',
        category: 'plucked',
        subcategory: 'strings',
        toneType: 'PluckSynth',
        behavior: 'normal',
        looping: true,
        pattern: 'arpeggio',
        patternSpeed: 4,
        physicsModulation: {
            velocityToDampening: true,
            positionToDetune: true
        },
        options: {
            attackNoise: 2,
            dampening: 3000,
            resonance: 0.96
        }
    },
    {
        id: 'cat_pluck_koto',
        name: 'Koto',
        icon: '♬',
        color: '#bc8f8f',
        category: 'plucked',
        subcategory: 'strings',
        toneType: 'PluckSynth',
        behavior: 'agile',
        looping: true,
        pattern: 'pentatonic',
        patternSpeed: 3,
        physicsModulation: {
            velocityToDampening: true,
            connectionsToResonance: true
        },
        options: {
            attackNoise: 3,
            dampening: 5000,
            resonance: 0.94
        }
    },

    // --- Subcategory: Keys ---
    {
        id: 'cat_pluck_piano',
        name: 'Piano',
        icon: '♯',
        color: '#f5f5dc',
        category: 'plucked',
        subcategory: 'keys',
        toneType: 'Synth',
        behavior: 'normal',
        looping: true,
        pattern: 'chord',
        patternSpeed: 1,
        physicsModulation: {
            velocityToVolume: true,
            connectionsToReverb: true
        },
        options: {
            oscillator: { type: "triangle" },
            envelope: { attack: 0.01, decay: 0.8, sustain: 0.2, release: 1.5 }
        }
    },
    {
        id: 'cat_pluck_rhodes',
        name: 'Rhodes',
        icon: '♭',
        color: '#ffe4b5',
        category: 'plucked',
        subcategory: 'keys',
        toneType: 'FMSynth',
        behavior: 'normal',
        looping: true,
        pattern: 'majorTriad',
        patternSpeed: 0.5,
        physicsModulation: {
            positionToHarmonicity: true,
            connectionsToReverb: true
        },
        options: {
            harmonicity: 3.5,
            modulationIndex: 2,
            oscillator: { type: "sine" },
            envelope: { attack: 0.01, decay: 0.6, sustain: 0.3, release: 1.2 },
            modulation: { type: "sine" },
            modulationEnvelope: { attack: 0.01, decay: 0.5, sustain: 0.2, release: 1 }
        }
    },
    {
        id: 'cat_pluck_marimba',
        name: 'Marimba',
        icon: '⊕',
        color: '#d2691e',
        category: 'plucked',
        subcategory: 'keys',
        toneType: 'FMSynth',
        behavior: 'normal',
        looping: true,
        pattern: 'bounce',
        patternSpeed: 4,
        physicsModulation: {
            velocityToPitch: true,
            positionToDetune: true
        },
        options: {
            harmonicity: 4,
            modulationIndex: 2,
            oscillator: { type: "sine" },
            envelope: { attack: 0.001, decay: 0.3, sustain: 0, release: 0.3 },
            modulation: { type: "sine" },
            modulationEnvelope: { attack: 0.001, decay: 0.2, sustain: 0, release: 0.2 }
        }
    },

    // ╔════════════════════════════════════════════════════════════════╗
    // ║                    CATEGORY: AMBIENT                           ║
    // ╚════════════════════════════════════════════════════════════════╝
    
    // --- Subcategory: Textures ---
    {
        id: 'cat_amb_wind',
        name: 'Wind',
        icon: '≋',
        color: '#b0e0e6',
        category: 'ambient',
        subcategory: 'textures',
        toneType: 'NoiseSynth',
        behavior: 'floaty',
        looping: true,
        pattern: 'breath',
        patternSpeed: 0.2,
        physicsModulation: {
            velocityToVolume: true
        },
        options: {
            noise: { type: 'pink' },
            envelope: { attack: 1, decay: 0.5, sustain: 0.5, release: 2 }
        }
    },
    {
        id: 'cat_amb_static',
        name: 'Static',
        icon: '▒',
        color: '#778899',
        category: 'ambient',
        subcategory: 'textures',
        toneType: 'NoiseSynth',
        behavior: 'normal',
        looping: true,
        pattern: 'rhythm',
        patternSpeed: 2,
        physicsModulation: {
            velocityToVolume: true
        },
        options: {
            noise: { type: 'white' },
            envelope: { attack: 0.01, decay: 0.1, sustain: 0.1, release: 0.2 }
        }
    },
    {
        id: 'cat_amb_rain',
        name: 'Rain',
        icon: '҂',
        color: '#4682b4',
        category: 'ambient',
        subcategory: 'textures',
        toneType: 'NoiseSynth',
        behavior: 'floaty',
        looping: true,
        pattern: 'sparse',
        patternSpeed: 1,
        physicsModulation: {
            velocityToVolume: true
        },
        options: {
            noise: { type: 'brown' },
            envelope: { attack: 0.1, decay: 0.3, sustain: 0.2, release: 0.5 }
        }
    },

    // --- Subcategory: Drones ---
    {
        id: 'cat_amb_ocean',
        name: 'Ocean',
        icon: '≈',
        color: '#006994',
        category: 'ambient',
        subcategory: 'drones',
        toneType: 'FMSynth',
        behavior: 'floaty',
        looping: true,
        pattern: 'drone',
        patternSpeed: 0.1,
        physicsModulation: {
            positionToHarmonicity: true,
            connectionsToModIndex: true,
            connectionsToReverb: true
        },
        options: {
            harmonicity: 0.25,
            modulationIndex: 6,
            oscillator: { type: "sine" },
            envelope: { attack: 4, decay: 2, sustain: 1, release: 5 },
            modulation: { type: "sine" },
            modulationEnvelope: { attack: 3, decay: 1, sustain: 1, release: 4 }
        }
    },
    {
        id: 'cat_amb_space',
        name: 'Space',
        icon: '✦',
        color: '#191970',
        category: 'ambient',
        subcategory: 'drones',
        toneType: 'AMSynth',
        behavior: 'floaty',
        looping: true,
        pattern: 'drone',
        patternSpeed: 0.05,
        physicsModulation: {
            positionToDetune: true,
            connectionsToReverb: true
        },
        options: {
            harmonicity: 4,
            oscillator: { type: "sine" },
            envelope: { attack: 5, decay: 2, sustain: 1, release: 6 },
            modulation: { type: "sine" }
        }
    },
    {
        id: 'cat_amb_cosmos',
        name: 'Cosmos',
        icon: '★',
        color: '#2e0854',
        category: 'ambient',
        subcategory: 'drones',
        toneType: 'DuoSynth',
        behavior: 'floaty',
        looping: true,
        pattern: 'sparse',
        patternSpeed: 0.1,
        physicsModulation: {
            connectionsToHarmonicity: true,
            velocityToVibratoAmount: true,
            connectionsToReverb: true
        },
        options: {
            vibratoAmount: 0.05,
            vibratoRate: 0.5,
            harmonicity: 1.5,
            voice0: { volume: -10, oscillator: { type: "sine" }, envelope: { attack: 3, decay: 2, sustain: 1, release: 4 } },
            voice1: { volume: -12, oscillator: { type: "sine" }, envelope: { attack: 4, decay: 2, sustain: 0.8, release: 5 } }
        }
    },

    // ╔════════════════════════════════════════════════════════════════╗
    // ║                    CATEGORY: ARPEGGIOS                         ║
    // ╚════════════════════════════════════════════════════════════════╝
    
    // --- Subcategory: Classic ---
    {
        id: 'cat_arp_classic',
        name: 'Classic Arp',
        icon: '↗',
        color: '#ff69b4',
        category: 'arpeggios',
        subcategory: 'classic',
        toneType: 'Synth',
        behavior: 'agile',
        looping: true,
        pattern: 'ascend',
        patternSpeed: 6,
        physicsModulation: {
            velocityToPitch: true,
            positionToDetune: true
        },
        options: {
            oscillator: { type: "triangle" },
            envelope: { attack: 0.01, decay: 0.15, sustain: 0.1, release: 0.2 }
        }
    },
    {
        id: 'cat_arp_down',
        name: 'Descend',
        icon: '↘',
        color: '#db7093',
        category: 'arpeggios',
        subcategory: 'classic',
        toneType: 'Synth',
        behavior: 'agile',
        looping: true,
        pattern: 'descend',
        patternSpeed: 6,
        physicsModulation: {
            velocityToPitch: true,
            connectionsToReverb: true
        },
        options: {
            oscillator: { type: "sawtooth" },
            envelope: { attack: 0.01, decay: 0.15, sustain: 0.1, release: 0.2 }
        }
    },
    {
        id: 'cat_arp_random',
        name: 'Chaos',
        icon: '⟳',
        color: '#ff1493',
        category: 'arpeggios',
        subcategory: 'classic',
        toneType: 'Synth',
        behavior: 'agile',
        looping: true,
        pattern: 'random8',
        patternSpeed: 5,
        physicsModulation: {
            velocityToPitch: true,
            positionToDetune: true,
            connectionsToPattern: true
        },
        options: {
            oscillator: { type: "square" },
            envelope: { attack: 0.01, decay: 0.1, sustain: 0.15, release: 0.2 }
        }
    },

    // --- Subcategory: Complex ---
    {
        id: 'cat_arp_spiral',
        name: 'Spiral',
        icon: '◌',
        color: '#ba55d3',
        category: 'arpeggios',
        subcategory: 'complex',
        toneType: 'FMSynth',
        behavior: 'agile',
        looping: true,
        pattern: 'spiral',
        patternSpeed: 4,
        physicsModulation: {
            velocityToPitch: true,
            positionToHarmonicity: true,
            connectionsToModIndex: true
        },
        options: {
            harmonicity: 2,
            modulationIndex: 4,
            oscillator: { type: "sine" },
            envelope: { attack: 0.01, decay: 0.2, sustain: 0.2, release: 0.3 },
            modulation: { type: "sine" },
            modulationEnvelope: { attack: 0.01, decay: 0.15, sustain: 0.2, release: 0.25 }
        }
    },
    {
        id: 'cat_arp_fib',
        name: 'Fibonacci',
        icon: '❀',
        color: '#9932cc',
        category: 'arpeggios',
        subcategory: 'complex',
        toneType: 'Synth',
        behavior: 'normal',
        looping: true,
        pattern: 'fibonacci',
        patternSpeed: 3,
        physicsModulation: {
            velocityToPitch: true,
            positionToDetune: true,
            connectionsToReverb: true
        },
        options: {
            oscillator: { type: "sine" },
            envelope: { attack: 0.02, decay: 0.25, sustain: 0.15, release: 0.4 }
        }
    },
    {
        id: 'cat_arp_primes',
        name: 'Primes',
        icon: '⊗',
        color: '#8b008b',
        category: 'arpeggios',
        subcategory: 'complex',
        toneType: 'AMSynth',
        behavior: 'normal',
        looping: true,
        pattern: 'primes',
        patternSpeed: 3,
        physicsModulation: {
            velocityToPitch: true,
            connectionsToReverb: true
        },
        options: {
            harmonicity: 3,
            oscillator: { type: "sine" },
            envelope: { attack: 0.02, decay: 0.2, sustain: 0.2, release: 0.35 },
            modulation: { type: "sine" }
        }
    },

    // ╔════════════════════════════════════════════════════════════════╗
    // ║                    CATEGORY: EFFECTS                           ║
    // ╚════════════════════════════════════════════════════════════════╝
    
    // --- Subcategory: Risers ---
    {
        id: 'cat_fx_riser',
        name: 'Riser',
        icon: '▲',
        color: '#00ff00',
        category: 'effects',
        subcategory: 'risers',
        toneType: 'Synth',
        behavior: 'floaty',
        looping: true,
        pattern: 'ascend',
        patternSpeed: 0.5,
        physicsModulation: {
            velocityToPitch: true,
            positionToDetune: true
        },
        options: {
            oscillator: { type: "sawtooth" },
            envelope: { attack: 2, decay: 0.5, sustain: 0.8, release: 1 }
        }
    },
    {
        id: 'cat_fx_sweep',
        name: 'Sweep',
        icon: '⤴',
        color: '#7cfc00',
        category: 'effects',
        subcategory: 'risers',
        toneType: 'MonoSynth',
        behavior: 'floaty',
        looping: true,
        pattern: 'wave',
        patternSpeed: 0.3,
        physicsModulation: {
            velocityToFilter: true,
            positionToDetune: true
        },
        options: {
            oscillator: { type: "sawtooth" },
            envelope: { attack: 1.5, decay: 0.8, sustain: 0.6, release: 1.5 },
            filterEnvelope: { attack: 2, decay: 1, sustain: 0.5, baseFrequency: 100, octaves: 5 }
        }
    },

    // --- Subcategory: Impacts ---
    {
        id: 'cat_fx_impact',
        name: 'Impact',
        icon: '◈',
        color: '#ff6347',
        category: 'effects',
        subcategory: 'impacts',
        toneType: 'MembraneSynth',
        behavior: 'heavy',
        looping: true,
        pattern: 'sparse',
        patternSpeed: 0.25,
        physicsModulation: {
            velocityToVolume: true
        },
        options: {
            pitchDecay: 0.2,
            octaves: 10,
            oscillator: { type: "sine" },
            envelope: { attack: 0.001, decay: 1, sustain: 0, release: 2 }
        }
    },
    {
        id: 'cat_fx_crash',
        name: 'Crash',
        icon: '✧',
        color: '#ff4500',
        category: 'effects',
        subcategory: 'impacts',
        toneType: 'MetalSynth',
        behavior: 'normal',
        looping: true,
        pattern: 'breath',
        patternSpeed: 0.125,
        physicsModulation: {
            velocityToVolume: true,
            connectionsToReverb: true
        },
        options: {
            frequency: 150,
            envelope: { attack: 0.001, decay: 0.8, release: 0.5 },
            harmonicity: 2,
            modulationIndex: 50,
            resonance: 2000,
            octaves: 2
        }
    },

    // --- Subcategory: Glitches ---
    {
        id: 'cat_fx_glitch',
        name: 'Glitch',
        icon: '▦',
        color: '#00ced1',
        category: 'effects',
        subcategory: 'glitches',
        toneType: 'Synth',
        behavior: 'agile',
        looping: true,
        pattern: 'breakbeat',
        patternSpeed: 8,
        physicsModulation: {
            velocityToPitch: true,
            positionToDetune: true,
            connectionsToPattern: true
        },
        options: {
            oscillator: { type: "square" },
            envelope: { attack: 0.001, decay: 0.03, sustain: 0, release: 0.02 }
        }
    },
    {
        id: 'cat_fx_stutter',
        name: 'Stutter',
        icon: '▤',
        color: '#40e0d0',
        category: 'effects',
        subcategory: 'glitches',
        toneType: 'Synth',
        behavior: 'agile',
        looping: true,
        pattern: 'triplet',
        patternSpeed: 12,
        physicsModulation: {
            velocityToVolume: true,
            positionToDetune: true
        },
        options: {
            oscillator: { type: "sawtooth" },
            envelope: { attack: 0.001, decay: 0.02, sustain: 0.1, release: 0.02 }
        }
    },
    {
        id: 'cat_fx_bit',
        name: 'Bitcrush',
        icon: '▥',
        color: '#48d1cc',
        category: 'effects',
        subcategory: 'glitches',
        toneType: 'Synth',
        behavior: 'normal',
        looping: true,
        pattern: 'habanera',
        patternSpeed: 4,
        physicsModulation: {
            velocityToPitch: true,
            connectionsToPattern: true
        },
        options: {
            oscillator: { type: "square" },
            envelope: { attack: 0.001, decay: 0.1, sustain: 0.2, release: 0.1 }
        }
    }
];

// Instrument categories for UI organization
export const INSTRUMENT_CATEGORIES = {
    bass: {
        name: 'Bass',
        icon: '🔊',
        subcategories: ['deep', 'punchy', 'synth']
    },
    leads: {
        name: 'Leads',
        icon: '✦',
        subcategories: ['bright', 'warm', 'aggressive']
    },
    pads: {
        name: 'Pads',
        icon: '☁',
        subcategories: ['ethereal', 'dark', 'evolving']
    },
    percussion: {
        name: 'Percussion',
        icon: '🥁',
        subcategories: ['kicks', 'metallic', 'electronic']
    },
    plucked: {
        name: 'Plucked',
        icon: '🎸',
        subcategories: ['strings', 'keys']
    },
    ambient: {
        name: 'Ambient',
        icon: '🌊',
        subcategories: ['textures', 'drones']
    },
    arpeggios: {
        name: 'Arpeggios',
        icon: '🔄',
        subcategories: ['classic', 'complex']
    },
    effects: {
        name: 'Effects',
        icon: '✨',
        subcategories: ['risers', 'impacts', 'glitches']
    }
};
